import { useEffect, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useQueryParams } from '@atlassian/jira-software-router-utils/src/services/query-params/index.tsx';
import { type DateFilter, defaultDateFilter, useDateFilterStore } from '../date-filter';
import { useTableFilterStore } from '../top-filter';
import {
	QUERY_PARAM_TEXT,
	QUERY_PARAM_ASSIGNEE,
	QUERY_PARAM_ENVIRONMENT_NAME,
	QUERY_PARAM_EPIC,
	QUERY_PARAM_ISSUE_TYPE,
	QUERY_PARAM_FIX_VERSION,
	QUERY_PARAM_START_DATE,
	QUERY_PARAM_END_DATE,
} from './constants';
import {
	useQueryParamsFromFilters,
	getFiltersFromQueryParams,
	getDateFilterFromQueryParams,
	filtersToQueryParams,
	queryParamDateRange,
} from './utils';

export const useQueryParamSync = () => {
	const [topFilterState, topFilterActions] = useTableFilterStore();
	const [dateFilterState, dateFilterActions] = useDateFilterStore();
	const [queryParams, setQueryParams] = useQueryParams(
		QUERY_PARAM_TEXT,
		QUERY_PARAM_ASSIGNEE,
		QUERY_PARAM_ENVIRONMENT_NAME,
		QUERY_PARAM_EPIC,
		QUERY_PARAM_ISSUE_TYPE,
		QUERY_PARAM_FIX_VERSION,
		QUERY_PARAM_START_DATE,
		QUERY_PARAM_END_DATE,
	);

	const dateFilterFromQuery: {
		dateFilter: DateFilter;
	} = useMemo(() => {
		try {
			return getDateFilterFromQueryParams(queryParams);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			// a user will end up here if they enter a query string with an invalid CUSTOM date format
			// don't let a bad date filter kill the website entirely, just reset it

			fireErrorAnalytics({
				meta: {
					id: 'invalidDateFilterQueryString',
					packageName: 'jiraSoftwareDeployments',
					teamName: 'fusion-solaris',
				},
			});

			dateFilterActions.resetFilter();

			// need to return default value for use in current render
			return { dateFilter: defaultDateFilter };
		}
	}, [queryParams, dateFilterActions]);

	const topFilterFromQuery = useMemo(() => getFiltersFromQueryParams(queryParams), [queryParams]);

	topFilterFromQuery.environmentTypeConcatNames = topFilterState.environmentTypeConcatNames;

	const filtersFromQuery = useMemo(
		() => ({
			...topFilterFromQuery,
			...omit(dateFilterFromQuery, ['type']),
		}),
		[topFilterFromQuery, dateFilterFromQuery],
	);
	const filtersRef = useRef(filtersFromQuery);

	const queryFromFilters = useQueryParamsFromFilters();
	const queryRef = useRef(queryFromFilters);

	// *** Set the initial state *** \\
	// on initial load Query string is the source of truth
	// This will run on the server render and the initial client render
	const initialLoad = useRef(true);
	if (initialLoad.current && Object.values(queryParams).some((value) => value !== undefined)) {
		topFilterActions.setFilter(topFilterFromQuery);
		dateFilterActions.setDateFilter(dateFilterFromQuery.dateFilter);
	}

	// Effect to update query params using History Replace on initial render
	useEffect(() => {
		if (initialLoad.current) {
			setQueryParams(
				filtersToQueryParams(
					topFilterFromQuery,
					queryParamDateRange(dateFilterFromQuery.dateFilter),
				),
				{ sort: false, updateType: 'replace' },
			);
			initialLoad.current = false;
		}
	}, [setQueryParams, topFilterFromQuery, dateFilterFromQuery]);

	// Effect to sync State to match updated Query
	useEffect(() => {
		const {
			dateFilter: { startDate, endDate },
		} = dateFilterState;
		const filtersFromState = { ...topFilterState, startDate, endDate };
		// if filtersRef is not in sync with latest value of filtersFromQuery
		if (!isEqual(filtersFromQuery, filtersRef.current)) {
			// if query params not in sync with filters
			if (!isEqual(filtersFromQuery, filtersFromState)) {
				topFilterActions.setFilter(topFilterFromQuery);
				dateFilterActions.setDateFilter(dateFilterFromQuery.dateFilter);
			}
			filtersRef.current = filtersFromQuery;
		}
	}, [
		filtersFromQuery,
		topFilterFromQuery,
		dateFilterFromQuery,
		topFilterState,
		dateFilterState,
		topFilterActions,
		dateFilterActions,
	]);

	// Effect to sync Query to match updated State
	useEffect(() => {
		// if queryFromFilters is not in sync with latest value
		if (!isEqual(queryFromFilters, queryRef.current)) {
			// if queryRef is not in sync with latest value of queryFromFilters
			if (!isEqual(queryParams, queryFromFilters)) {
				setQueryParams(queryFromFilters, { sort: false });
			}
			queryRef.current = queryFromFilters;
		}
	}, [queryParams, queryFromFilters, setQueryParams]);
};

export default useQueryParamSync;
